import { motion } from "framer-motion";
import heroImage from "@/assets/hero-main.webp";

interface Props {
  onStart: () => void;
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" as const },
  }),
};

const steps = [
  { num: "1", text: "Вы рассказываете о ситуации" },
  { num: "2", text: "Мы создаём историю" },
  { num: "3", text: "Вы читаете её вместе" },
];

const situations = [
  "боится темноты",
  "ревнует к младшему",
  "не хочет идти в садик",
  "переживает перемены",
];

const HeroSection = ({ onStart }: Props) => (
  <div className="bg-background">
    {/* Hero */}
    <div className="relative min-h-screen flex items-end justify-center overflow-hidden pb-24 md:items-center md:pb-0">
      <img
        src={heroImage}
        alt="Родитель читает ребёнку сказку"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Stronger darkening gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/55 to-black/20" />

      <motion.div
        initial="hidden"
        animate="visible"
        className="relative z-10 text-center px-6 max-w-2xl"
      >
        <motion.p
          custom={0}
          variants={fadeUp}
          className="text-sm font-sans tracking-widest text-white mb-3 uppercase drop-shadow-md"
        >
          Тихая Сказка
        </motion.p>

        <motion.h1
          custom={1}
          variants={fadeUp}
          className="text-h1 font-serif text-white mb-4 leading-tight drop-shadow-lg"
        >
          Помогите ребёнку справиться со страхами через персональную историю
        </motion.h1>

        <motion.p
          custom={2}
          variants={fadeUp}
          className="text-lg text-white/90 mb-10 font-sans leading-relaxed drop-shadow-md"
        >
          История, в которой он или она&nbsp;— главный герой.
          <br />
          Создаётся за 5 минут.
        </motion.p>

        {/* CTA with glow */}
        <motion.div custom={3} variants={fadeUp}>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={onStart}
            className="relative bg-primary text-foreground px-10 py-4 rounded-2xl text-lg font-sans font-semibold shadow-[0_0_30px_hsl(var(--primary)/0.45)] hover:shadow-[0_0_40px_hsl(var(--primary)/0.6)] transition-shadow"
          >
            Создать историю
          </motion.button>
        </motion.div>

        <motion.p
          custom={4}
          variants={fadeUp}
          className="mt-4 text-sm text-white/80 font-sans drop-shadow-md"
        >
          149&nbsp;₽ за 3 персональные истории
        </motion.p>

        <motion.p
          custom={5}
          variants={fadeUp}
          className="mt-2 text-xs text-white/60 font-sans drop-shadow-md"
        >
          Уже создано 2&nbsp;438 историй для родителей
        </motion.p>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-5 h-8 rounded-full border-2 border-white/50 flex items-start justify-center pt-1.5"
        >
          <div className="w-1 h-1.5 rounded-full bg-white/70" />
        </motion.div>
      </motion.div>
    </div>

    {/* How it works */}
    <section className="py-20 px-6">
      <div className="max-w-xl mx-auto text-center">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-h2 font-serif text-foreground mb-12"
        >
          Как это работает
        </motion.h2>

        <div className="space-y-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.5 }}
              className="flex items-center gap-4 text-left"
            >
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/15 text-primary font-sans font-semibold text-sm flex items-center justify-center">
                {s.num}
              </span>
              <span className="text-body font-sans text-foreground">{s.text}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Situations */}
    <section className="py-16 px-6 bg-muted/40">
      <div className="max-w-xl mx-auto text-center">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-h2 font-serif text-foreground mb-10"
        >
          Подходит, если ребёнок:
        </motion.h2>

        <ul className="space-y-4 text-left max-w-sm mx-auto">
          {situations.map((s, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              className="flex items-center gap-3 text-body font-sans text-foreground"
            >
              <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              {s}
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  </div>
);

export default HeroSection;
